import { useState, useEffect } from 'react';
import {
  Flex,
  Spinner,
  Text,
  Button,
  useBreakpointValue,
  Switch,
  Tooltip,
} from '@chakra-ui/react';
import { FiRefreshCw, FiMenu, FiX, FiTrash2, FiPaperclip, FiSend, FiMic, FiMessageSquare } from 'react-icons/fi';
import { FixedChatLayout } from '../../components/v0/FixedChatLayout';
import { useTranslation } from 'react-i18next';
import { useAtom, useSetAtom } from 'jotai';
import { addToastAtom, sendMessageAtom } from '../../atoms/v0';
import { streamingChatEnabledAtom } from '../../atoms/v0/chatAtoms';
import { ConversationSidebar, ChatArea } from '../../components/v0/chat';
import { useChat, useImageUpload, useWebSpeechRecognition, useWhisperRecognition } from '../../hooks/v0/chat';
import { trackEvent } from '../../lib/analytics';
import { QuickPrompts, EmptyStateQuickPrompts } from '../../components/v0/chat/QuickPrompts';
import CharacterSelector from '../../components/v0/chat/CharacterSelector';
import { MessageList } from '../../components/v0/chat/MessageList';
import { InlineSpinner } from '../../components/v0/chat/InlineSpinner';

// Simple Tailwind Spinner component
const LoadingSpinner = () => (
  <svg className="animate-spin h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

export const Chat = () => {
  // 翻译hook
  const { t } = useTranslation(['v0/chat', 'v0/common', 'common']);

  // 响应式布局
  const isMobile = useBreakpointValue({ base: true, md: false });
  const sidebarWidth = useBreakpointValue({ base: '100%', md: '280px' });

  // 全局toast
  const addToast = useSetAtom(addToastAtom);

  // 流式聊天开关
  const [streamingChatEnabled, setStreamingChatEnabled] = useAtom(streamingChatEnabledAtom);

  // 聊天state和方法
  const {
    conversations,
    selectedConversationId,
    setSelectedConversationId,
    selectedConversation,
    messages,
    conversationsLoadable,
    conversationsLoading,
    conversationLoading,
    isSending,
    loadingMore,
    hasMoreMessages,
    fetchConversations,
    fetchConversation,
    loadMoreMessages,
    sendMessage,
    deleteConversation,
    createNewConversation,
  } = useChat();

  // 图片上传相关
  const {
    imageFile,
    imagePreview,
    fileInputRef,
    handleFileChange,
    handleUploadClick,
    clearSelectedImage,
    uploadImage
  } = useImageUpload();

  // 本地状态
  const [input, setInput] = useState('');
  const [showSidebar, setShowSidebar] = useState(() => !isMobile);

  // 获取 sendMessage action atom 的 setter
  const triggerSendMessage = useSetAtom(sendMessageAtom);

  // Web Speech API 语音识别
  const {
    transcript: webSpeechTranscript,
    isListening: isWebSpeechListening,
    audioLevel,
    currentLangCode,
    startListening: startWebSpeechListening,
    stopListening: stopWebSpeechListening,
    browserSupportsSpeechRecognition
  } = useWebSpeechRecognition();

  // Whisper API 语音识别
  const {
    isRecording,
    isProcessing,
    transcript: whisperTranscript,
    startRecording,
    stopRecording,
    cancelRecording
  } = useWhisperRecognition();

  // 初始加载
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // 响应式布局调整
  useEffect(() => {
    setShowSidebar(!isMobile);
  }, [isMobile]);

  // 处理 Web Speech API 语音识别结果
  useEffect(() => {
    console.log('webSpeechTranscript changed:', webSpeechTranscript);
    if (webSpeechTranscript) {
      console.log('Setting input to transcript:', webSpeechTranscript);
      setInput(webSpeechTranscript);
    }
  }, [webSpeechTranscript]);

  // 处理 Whisper API 语音识别结果
  useEffect(() => {
    if (whisperTranscript) {
      setInput(whisperTranscript);
    }
  }, [whisperTranscript]);

  // 处理新建对话
  const handleCreateNewConversation = () => {
    createNewConversation();

    // 清除输入和图片
    setInput('');
    clearSelectedImage();

    if (isMobile) {
      setShowSidebar(false); // Close sidebar on mobile when creating new chat
    }

    addToast({
      title: t('v0/chat:newConversationCreated'),
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  // 选择对话
  const handleSelectConversation = (conversationId: string) => {
    if (conversationId === selectedConversationId && !isMobile) return; // Don't re-select on desktop unless forced

    setSelectedConversationId(conversationId);
    // Only fetch if not already selected, or if mobile view changes
    if (conversationId !== selectedConversationId || isMobile) {
      fetchConversation(conversationId);
    }

    if (isMobile) {
      setShowSidebar(false); // Close sidebar on mobile after selection
    }
    setInput('');
    clearSelectedImage();
  };

  // 处理快速提示点击
  const handleQuickPromptSelect = (prompt: string) => {
    if (!selectedConversationId || conversationLoading || isSending) return;

    // 记录快速提示使用事件
    trackEvent('quick prompt used', {
      prompt: prompt.substring(0, 50), // 只记录前50个字符，避免过长
      conversation_id: selectedConversationId === 'new' ? 'new' : selectedConversationId
    });

    // 直接调用 sendMessage action atom 发送消息
    triggerSendMessage({ text: prompt });
    // 清空输入框 (可选, 如果希望点击后清空)
    // setInput('');
  };

  // 发送消息
  const handleSend = async () => {
    if ((!input.trim() && !imageFile)) return;

    const currentInput = input.trim();
    const currentImageFile = imageFile;
    const hasImage = !!currentImageFile;
    const messageLength = currentInput.length;
    const conversationId = selectedConversationId;

    // 记录消息发送尝试事件
    trackEvent('chat message attempt', {
      conversation_id: conversationId === 'new' ? 'new' : conversationId,
      message_length: messageLength,
      has_image: hasImage,
      streaming_enabled: streamingChatEnabled
    });

    // Clear input and preview immediately for responsiveness
    setInput('');
    clearSelectedImage(); // Clears preview, keeps file reference

    // Reset file input element
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    try {
      let mediaId: string | undefined;
      if (currentImageFile) {
        try {
          // Show uploading indicator?
          mediaId = await uploadImage(currentImageFile);

          // 记录图片上传成功事件
          trackEvent('image upload success', {
            conversation_id: conversationId === 'new' ? 'new' : conversationId,
            image_size: currentImageFile.size,
            image_type: currentImageFile.type
          });
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);

          // 记录图片上传失败事件
          trackEvent('image upload failed', {
            conversation_id: conversationId === 'new' ? 'new' : conversationId,
            image_size: currentImageFile.size,
            image_type: currentImageFile.type,
            error: uploadError instanceof Error ? uploadError.message : String(uploadError)
          });

          addToast({
            title: t('v0/chat:imageUploadFailed'),
            description: t('v0/chat:imageUploadFailedDescription'),
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return; // Stop message sending if upload fails
        }
      }

      await sendMessage({
        text: currentInput,
        mediaId
      });

      // 记录消息发送成功事件
      trackEvent('chat message sent', {
        conversation_id: conversationId === 'new' ? 'new' : conversationId,
        message_length: messageLength,
        has_image: hasImage,
        streaming_enabled: streamingChatEnabled
      });

    } catch (err) {
      console.error('Error sending message:', err);

      // 记录消息发送失败事件
      trackEvent('chat message failed', {
        conversation_id: conversationId === 'new' ? 'new' : conversationId,
        message_length: messageLength,
        has_image: hasImage,
        streaming_enabled: streamingChatEnabled,
        error: err instanceof Error ? err.message : String(err)
      });

      addToast({
        title: t('v0/chat:sendFailed'),
        description: t('v0/chat:sendFailedDescription'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      // Ensure image state is fully cleared even if send fails after upload success
      clearSelectedImage();
    }
  };

  // 处理删除对话
  const handleDeleteConversation = async (conversationId: string) => {
    if (!conversationId) return;
    if (window.confirm(t('v0/chat:confirmDelete'))) {
      // 记录删除对话尝试事件
      trackEvent('conversation delete attempt', {
        conversation_id: conversationId
      });

      try {
        await deleteConversation(conversationId);

        // 记录删除对话成功事件
        trackEvent('conversation deleted', {
          conversation_id: conversationId
        });

        addToast({
          title: t('v0/chat:deleteSuccess'),
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        // If deleting the selected chat on mobile, ensure sidebar closes if it was open
        if (isMobile && selectedConversationId === conversationId) {
          setShowSidebar(false);
        }
      } catch (err) {
        console.error('Failed to delete conversation:', err);

        // 记录删除对话失败事件
        trackEvent('conversation delete failed', {
          conversation_id: conversationId,
          error: err instanceof Error ? err.message : String(err)
        });

        addToast({
          title: t('v0/chat:deleteFailed'),
          description: t('v0/chat:deleteFailedDescription'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  // 处理键盘按键
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSending) {
      e.preventDefault();
      handleSend();
    }
  };

  // 处理 Web Speech API 语音识别
  const handleWebSpeechRecognition = async () => {
    console.log('handleWebSpeechRecognition called, current state:', { isWebSpeechListening });

    if (isWebSpeechListening) {
      console.log('Stopping Web Speech recognition...');
      try {
        // 停止语音识别
        stopWebSpeechListening();

        // iOS 兼容性：强制更新 UI 状态
        const { isIOSPlatform } = await import('../../utils/platformUtils');
        if (isIOSPlatform()) {
          console.log('iOS detected, forcing UI update');
          // 在 iOS 上，可能需要手动强制更新 UI
          setTimeout(() => {
            if (isWebSpeechListening) {
              console.log('iOS: Still listening after stop, forcing state update');
              // 如果状态没有更新，可以尝试重新渲染组件
              setInput(input => input + ''); // 触发重新渲染
            }
          }, 500);
        }
      } catch (error) {
        console.error('Error stopping Web Speech recognition:', error);
        // 即使出错，也要尝试重置状态
        addToast({
          title: t('v0/chat:speechRecognitionStopFailed'),
          description: String(error),
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      }
    } else {
      console.log('Starting Web Speech recognition...');
      try {
        await startWebSpeechListening();
        console.log('Web Speech recognition started successfully from handler');
      } catch (error) {
        console.error('Error starting Web Speech recognition from handler:', error);
        addToast({
          title: t('v0/chat:speechRecognitionFailed'),
          description: String(error),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  // 处理 Whisper API 语音识别
  const handleWhisperRecognition = async () => {
    if (isRecording) {
      await stopRecording();
      // 转写结果会通过 useEffect 自动设置到输入框
    } else {
      await startRecording();
    }
  };

  // Chat.tsx still needs h-full for its main content div
  const mainContentHeight = 'h-full';

  // 获取当前标题
  const getTitle = () => {
    // NITEL 暂时不实现动态标题在这一块
    // if (conversationsLoadable.state === 'loading' && conversations.length === 0) {
    //   return t('v0/chat:loadingText');
    // }
    // if (selectedConversation) {
    //   return selectedConversation.title || t('v0/chat:newConversation');
    // }
    return t('v0/chat:title', 'AI 助手');
  };

  return (
    <FixedChatLayout
      bgColor="bg-bg-light dark:bg-bg-dark"
      withPadding={false}
      title={getTitle()}
    >
      <div className={`flex ${mainContentHeight} overflow-hidden relative`}>
        {/* Sidebar Container */}
        <div
          className={`
            ${isMobile ? 'absolute top-0 left-0 bottom-0 z-40' : 'relative z-auto'}
            ${isMobile ? (showSidebar ? 'w-full sm:w-3/4' : 'w-0') : (showSidebar ? 'w-[280px]' : 'w-0')}
            h-full flex-shrink-0
            bg-surface-light dark:bg-surface-dark
            border-r border-border-light dark:border-border-dark
            overflow-y-auto overflow-x-hidden
            transition-all duration-300 ease-in-out
            ${showSidebar ? 'opacity-100' : 'opacity-0 pointer-events-none'}
          `}
          aria-hidden={!showSidebar}
        >
          <ConversationSidebar
            conversations={conversations}
            selectedConversationId={selectedConversationId}
            conversationsLoading={conversationsLoading}
            conversationLoading={conversationLoading}
            handleSelectConversation={handleSelectConversation}
            handleCreateNewConversation={handleCreateNewConversation}
            isMobile={isMobile || false}
            onClose={() => setShowSidebar(false)}
          />
        </div>

        {/* Mobile Overlay Click Catcher */}
        {isMobile && showSidebar && (
          <div
            className="absolute inset-0 z-30 bg-black/30 backdrop-blur-sm"
            onClick={() => setShowSidebar(false)}
            aria-hidden="true"
          />
        )}

        {/* Main Chat Area Container */}
        <div className={`flex-1 flex flex-col relative overflow-hidden bg-bg-light dark:bg-bg-dark ${isMobile && showSidebar ? 'pointer-events-none opacity-50' : ''}`}>
          {/* Conditional Rendering: Loading / Error / Chat Content */}
          {conversationsLoadable.state === 'loading' && conversations.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="flex flex-col items-center">
                <LoadingSpinner />
                <p className="mt-2 text-gray-500 dark:text-gray-400">{t('v0/chat:loadingText')}</p>
              </div>
            </div>
          ) : conversationsLoadable.state === 'hasError' ? (
            <div className="flex-1 flex items-center justify-center p-8 text-center">
              <div className="flex flex-col items-center">
                <p className="mb-4 text-red-500">{t('v0/chat:loadFailed')}</p>
                <button
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  onClick={() => fetchConversations()}
                  disabled={conversationsLoading}
                >
                  <FiRefreshCw className={`-ml-1 mr-2 h-5 w-5 ${conversationsLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
                  {t('v0/common:refresh.refreshButton')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              <div className="flex-shrink-0 flex items-center justify-between px-4 h-14 border-b border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark">
                <div className="flex items-center flex-1 min-w-0">
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setShowSidebar(!showSidebar)}
                      className="p-2 -ml-2 mr-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500"
                      aria-label={t('v0/chat:toggleSidebar')}
                    >
                      <FiMenu className="h-6 w-6" aria-hidden="true" />
                    </button>
                  )}
                  <h2 className="text-base font-medium text-gray-900 dark:text-white truncate">
                    {selectedConversation?.title || t('v0/chat:newConversation')}
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <CharacterSelector />

                  {/* 流式聊天开关 */}
                  <Tooltip
                    label={t('v0/chat:streamingChat.description', '启用实时流式聊天响应')}
                    placement="bottom"
                    hasArrow
                  >
                    <Flex alignItems="center" mr={1} bg={streamingChatEnabled ? "teal.50" : "gray.50"}
                          px={2} py={1} borderRadius="md" border="1px solid"
                          borderColor={streamingChatEnabled ? "teal.200" : "gray.200"}>
                      <Text fontSize="xs" fontWeight="medium" color={streamingChatEnabled ? "teal.700" : "gray.600"} mr={1}>
                        {t('v0/chat:streamingMode')}
                      </Text>
                      <Switch
                        size="sm"
                        colorScheme="teal"
                        isChecked={streamingChatEnabled}
                        onChange={() => {
                          const newValue = !streamingChatEnabled;
                          setStreamingChatEnabled(newValue);
                          // 记录流式聊天开关事件
                          trackEvent('streaming chat toggled', {
                            enabled: newValue
                          });
                        }}
                        id="streaming-chat-toggle"
                        mr={1}
                      />
                      <FiMessageSquare
                        className="h-4 w-4"
                        color={streamingChatEnabled ? "#319795" : "currentColor"}
                      />
                    </Flex>
                  </Tooltip>

                  {selectedConversationId && (
                    <button
                      type="button"
                      onClick={() => handleDeleteConversation(selectedConversationId)}
                      disabled={conversationLoading}
                      className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700 dark:hover:text-red-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label={t('v0/chat:deleteConversation')}
                    >
                      <FiTrash2 className="h-5 w-5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>

              <MessageList
                messages={messages}
                loadingMore={loadingMore}
                hasMoreMessages={hasMoreMessages}
                loadMoreMessages={loadMoreMessages}
                isSending={isSending}
                conversationLoading={conversationLoading}
                selectedConversationId={selectedConversationId}
                emptyStateContent={
                  selectedConversationId ? undefined : (
                    <div className="flex flex-col items-center justify-center h-full">
                      <p className="text-center text-lg mb-4 text-gray-600 dark:text-gray-300">
                        {t('v0/chat:pleaseSelectConversation')}
                      </p>
                      <EmptyStateQuickPrompts onSelectPrompt={handleQuickPromptSelect} />
                    </div>
                  )
                }
              />

              <div className="flex-shrink-0 p-4 border-t border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark">
                {selectedConversationId && handleQuickPromptSelect && (
                  <div className="mb-3">
                    <QuickPrompts onSelectPrompt={handleQuickPromptSelect} />
                  </div>
                )}

                {imagePreview && (
                  <div className="relative w-20 h-20 mb-2 rounded border border-gray-300 dark:border-gray-600">
                    <img src={imagePreview} alt={t('v0/chat:preview')} className="object-cover w-full h-full rounded" />
                    <button
                      type="button"
                      onClick={clearSelectedImage}
                      className="absolute top-0 right-0 -mt-2 -mr-2 p-1 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                      aria-label={t('v0/common:remove')}
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={isSending || conversationLoading || isWebSpeechListening || isRecording || isProcessing}
                    className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={t('v0/chat:uploadImage')}
                  >
                    <FiPaperclip className="h-5 w-5" aria-hidden="true" />
                  </button>

                  {/* Web Speech API 语音按钮 */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={handleWebSpeechRecognition}
                      disabled={isSending || conversationLoading || !selectedConversationId || isRecording || isProcessing || !browserSupportsSpeechRecognition}
                      className={`p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isWebSpeechListening
                          ? 'text-white bg-green-500 hover:bg-green-600'
                          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      aria-label={isWebSpeechListening ? t('v0/chat:stopListening') : t('v0/chat:startListening')}
                    >
                      <div className="relative">
                        <FiMic className="h-5 w-5" aria-hidden="true" />
                        <div className="absolute -bottom-1 -right-1 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-full w-3 h-3">
                          <span className="text-[6px] font-bold text-gray-800 dark:text-gray-200">{currentLangCode}</span>
                        </div>
                      </div>
                    </button>

                    {/* 音频电平指示器 */}
                    {isWebSpeechListening && (
                      <div className="absolute -top-1 -right-1 flex space-x-0.5">
                        <div
                          className={`w-1 rounded-full ${audioLevel > 5 ? 'bg-green-500' : 'bg-gray-300'}`}
                          style={{ height: `${Math.min(4, Math.max(2, audioLevel / 20))}px` }}
                        />
                        <div
                          className={`w-1 rounded-full ${audioLevel > 15 ? 'bg-green-500' : 'bg-gray-300'}`}
                          style={{ height: `${Math.min(6, Math.max(2, audioLevel / 15))}px` }}
                        />
                        <div
                          className={`w-1 rounded-full ${audioLevel > 30 ? 'bg-green-500' : 'bg-gray-300'}`}
                          style={{ height: `${Math.min(8, Math.max(2, audioLevel / 10))}px` }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Whisper API 语音按钮 */}
                  <button
                    type="button"
                    onClick={handleWhisperRecognition}
                    disabled={isSending || conversationLoading || !selectedConversationId || isWebSpeechListening}
                    className={`p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isRecording || isProcessing
                        ? 'text-white bg-purple-500 hover:bg-purple-600'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    aria-label={isRecording ? t('v0/chat:stopRecording') : t('v0/chat:startRecording')}
                  >
                    <div className="relative">
                      <FiMic className="h-5 w-5" aria-hidden="true" />
                      <div className="absolute -top-1 -right-1 flex items-center justify-center bg-purple-100 dark:bg-purple-800 rounded-full w-3.5 h-3.5">
                        <span className="text-[6px] font-bold text-purple-800 dark:text-purple-100">AI</span>
                      </div>
                    </div>
                  </button>

                  <textarea
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      isWebSpeechListening
                        ? t('v0/chat:listeningPlaceholder')
                        : isRecording
                          ? t('v0/chat:recordingPlaceholder')
                          : isProcessing
                            ? t('v0/chat:processingPlaceholder')
                            : t('v0/chat:typeMessagePlaceholder')
                    }
                    disabled={isSending || conversationLoading || !selectedConversationId || isWebSpeechListening || isRecording || isProcessing}
                    className="flex-1 block w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed resize-none"
                  />

                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={isSending || conversationLoading || (!input.trim() && !imageFile) || isWebSpeechListening || isRecording || isProcessing}
                    className="inline-flex items-center justify-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={t('v0/chat:send')}
                  >
                    {isSending ? (
                      <InlineSpinner className="w-5 h-5" />
                    ) : (
                      <FiSend className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </FixedChatLayout>
  );
};